import { createContext, useState, useEffect, useContext, useMemo } from "react";
import lodash from "lodash";

// Api
import { getMarketingAgent } from "actions";
import {
  useGetListManagement,
  useGetLeadPrimaries,
} from "api/leads-management/query";

// Context
import { GlobalContext } from "contexts";

const UseLeadsManagementSateContext = createContext(null);
const UseLeadsManagementMethodContext = createContext(null);

const _LIMIT = 10;

const LEADS_STATUS = {
  secondary: "Secondary",
  ditanggapi: "Ditanggapi",
  primary: "Primary",
};

const SUB_LEADS_STATUS = {
  primary: "Primary",
  secondary: "Secondary",
};

const STATUS = [
  {
    label: "Semua Status",
    value: "",
  },
  {
    label: "Hot",
    value: "hot",
  },
  {
    label: "Warm",
    value: "warm",
  },
  {
    label: "Cold",
    value: "cold",
  },
];

export const LeadsManagementProvider = ({ children }) => {
  const { setShowLoading } = useContext(GlobalContext);

  const [params, setParams] = useState({
    page: 1,
    _limit: _LIMIT,
    lead_status: "Primary",
    sub_lead_status: "",
    full_name: "",
    status: STATUS[0],
    user_id: null,
  });

  const getListManagementParams = useMemo(() => {
    let res = {
      page: params.page,
      _limit: params._limit,
      full_name: params.full_name,
    };

    if (params.sub_lead_status === SUB_LEADS_STATUS.secondary && params.lead_status === LEADS_STATUS.ditanggapi) {
      res.lead_status = "Ditanggapi";
    } else if (params.lead_status === LEADS_STATUS.secondary) {
      res.lead_status = "Menunggu";
    }

    return res;
  }, [params]);

  const {
    data: dataListLeads,
    isFetching,
    refetch,
  } = useGetListManagement({
    params: getListManagementParams,
    enabled: Boolean(params.lead_status === LEADS_STATUS.secondary) || Boolean(params.lead_status === LEADS_STATUS.ditanggapi && params.sub_lead_status === SUB_LEADS_STATUS.secondary),
  });

  const getListPrimariesParams = useMemo(() => {
    let res = {
      page: params.page,
      _limit: params._limit,
    };

    if (params.lead_status === LEADS_STATUS.primary) {
      res.user_id = params.user_id;
    }
    if (params.lead_status === LEADS_STATUS.primary) {
      res.status = "waiting";
    }
    if (params.lead_status === LEADS_STATUS.ditanggapi && params.sub_lead_status === SUB_LEADS_STATUS.primary) {
      res.status = "responded";

      if(params.status.value) {
        res.status_lead = params.status.value;
      }
    }

    return res;
  }, [params]);

  const { data: dataLeadPrimaries } = useGetLeadPrimaries({
    params: getListPrimariesParams,
    enabled: Boolean(params.lead_status === LEADS_STATUS.primary) || Boolean(params.lead_status === LEADS_STATUS.ditanggapi && params.sub_lead_status === SUB_LEADS_STATUS.primary),
  });
  
  const totalPage = useMemo(() => {
    const data = (params.lead_status === LEADS_STATUS.primary) || (params.lead_status === LEADS_STATUS.ditanggapi && params.sub_lead_status === SUB_LEADS_STATUS.primary) ? dataLeadPrimaries : dataListLeads;
    
    if (!data) return 0;

    const totalItem = data?.headers?.["x-total"];
    const totalPage = lodash.ceil(totalItem / _LIMIT) || 0;

    return totalPage;
  }, [dataListLeads, dataLeadPrimaries, params.lead_status, params.sub_lead_status]);

  const totalData = useMemo(() => {
    const data = (params.lead_status === LEADS_STATUS.primary) || (params.lead_status === LEADS_STATUS.ditanggapi && params.sub_lead_status === SUB_LEADS_STATUS.primary) ? dataLeadPrimaries : dataListLeads;
    
    if (!data) return 0;

    return data?.headers?.["x-total"];
  }, [dataListLeads, dataLeadPrimaries, params.lead_status, params.sub_lead_status]);

  const listLeads = useMemo(() => {
    if (!dataListLeads) return [];

    return dataListLeads?.data?.data ?? [];
  }, [dataListLeads]);

  const listLeadPrimaries = useMemo(() => {
    if (!dataLeadPrimaries) return [];

    return dataLeadPrimaries?.data?.data ?? [];
  }, [dataLeadPrimaries]);

  const [agents, setAgents] = useState([]);

  const getAgents = async () => {
    try {
      const list = await getMarketingAgent("?page=1&_limit=99999");

      const newData = list?.data ?? [];

      setAgents(newData);
    } catch (error) {
      console.log(error);
    }
  };

  const handleNextPage = () => {
    if (params.page === totalPage) return;

    setParams({ ...params, page: params.page + 1 });
  };

  const handlePrevPage = () => {
    if (params.page === 1) return;

    setParams({ ...params, page: params.page - 1 });
  };

  const handleChangeLeadStatus = (status) => {
    let newParams = { ...params, page: 1, lead_status: status };

    if (status === "Ditanggapi") {
      newParams.sub_lead_status = "Primary";
    } else {
      newParams.sub_lead_status = "";
      newParams.status = STATUS[0];
      newParams.user_id = null;
      newParams.full_name = "";
    }

    setParams(newParams);
  };

  const handleReset = () => {
    setParams({ ...params, 
      page: 1, 
      full_name: "", 
      status: STATUS[0], 
      user_id: null 
    });
  };

  const handleChangeSubLeadStatus = (status) =>
    setParams({ ...params, page: 1, sub_lead_status: status });

  const handleChangeAgen = (agen) =>
    setParams({ ...params, page: 1, full_name: agen.full_name, user_id: agen.id });

  useEffect(() => {
    setShowLoading(isFetching);
  }, [isFetching]);

  useEffect(() => {
    getAgents();
  }, []);

  return (
    <UseLeadsManagementSateContext.Provider
      value={{
        LEADS_STATUS,
        listLeads,
        totalPage,
        agents,
        params,
        listLeadPrimaries,
        SUB_LEADS_STATUS,
        STATUS,
        totalData
      }}
    >
      <UseLeadsManagementMethodContext.Provider
        value={{
          handleNextPage,
          handlePrevPage,
          handleChangeLeadStatus,
          handleChangeAgen,
          setParams,
          handleGetLeads: refetch,
          handleChangeSubLeadStatus,
          handleReset
        }}
      >
        {children}
      </UseLeadsManagementMethodContext.Provider>
    </UseLeadsManagementSateContext.Provider>
  );
};

//Custom hooks for the values
export const useLeadsManagementState = () => {
  const ctx = useContext(UseLeadsManagementSateContext);

  if (!ctx) {
    throw new Error(
      "useLeadsManagementState must be used within the ERProvider"
    );
  }

  return ctx;
};

//Custom hooks for the functions
export const useLeadsManagementMethod = () => {
  const ctx = useContext(UseLeadsManagementMethodContext);

  if (!ctx) {
    throw new Error(
      "useLeadsManagementMethod must be used within the ERProvider"
    );
  }

  return ctx;
};
